import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApprovalState,
  ApprovalType,
  ContentChannel,
  Role,
  isExportOnlyPost,
  isPostOverdue,
} from '@mg/shared';
import type { ApprovalDecisionDto, Post,
  SubmitForApprovalDto,
} from '@mg/shared';
import { ArrowLeft, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { api, ApiRequestError } from '../lib/api';
import { isLostRequest, LOST_REQUEST_MESSAGE, useLostPublishWatch } from '../lib/lostRequest';
import { useAuth } from '../auth/AuthContext';
import { useTenantKey } from '../lib/useTenantKey';
import { invalidateContentQueries } from '../lib/contentCache';
import { useEffectiveApprovalType } from '../lib/useEffectiveApprovalType';
import {
  approvalStateLabel,
  approvalStateTone,
  enumLabel,
  formatDateTime,
  type PostDisplayState,
  failureChipState,
} from '../lib/display';
import { PageHeader } from '../components/shell/PageHeader';
import { TestModeBanner } from '../features/testMode/TestModeBanner';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { AlertBanner } from '../components/ui/AlertBanner';
import { Card, CardBody } from '../components/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/States';
import { useDialogs } from '../components/ui/DialogProvider';
import { PostGenerateDesignModal } from '../features/design/PostGenerateDesignModal';
import { PostCard, postDisplayState } from '../features/posts/PostCard';
import { publishConfirm } from '../features/posts/publishConfirm';
import {
  CampaignPickerModal,
  type CampaignPickerAction,
} from '../features/posts/CampaignPickerModal';
import { CampaignFormModal } from '../features/campaigns/CampaignFormModal';
import {
  ActionRow,
  ApprovalActionBar,
  SUBMITTABLE_STATES,
} from '../features/approval/ApprovalActionBar';
import {
  DecisionCommentModal,
  type DecisionCommentTarget,
} from '../features/approval/DecisionCommentModal';
import { campaignBadgeState, postStatusRank } from '../features/approval/statusTabs';

import { postBadgeState } from '../features/posts/PostsView';

const REVIEWER_ROLES = [
  Role.REVIEWER,
  Role.STAKEHOLDER,
  Role.COMPLIANCE_REVIEWER,
  Role.TENANT_ADMIN,
];
const AUTHOR_ROLES = [Role.CREATOR, Role.REVIEWER, Role.TENANT_ADMIN];

export function CampaignDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const tenantKey = useTenantKey();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { principal } = useAuth();
  const { confirm, alert } = useDialogs();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(true); // Approval history — expanded by default
  const [pendingDecision, setPendingDecision] = useState<DecisionCommentTarget | null>(null);
  /** Open campaign picker for a post card's ⋯ Duplicate / Move action. */
  const [picker, setPicker] = useState<{ action: CampaignPickerAction; post: Post } | null>(null);
  /** Post that Generate Design is open for. Null keeps the dialog closed. */
  const [designPost, setDesignPost] = useState<Post | null>(null);

  /**
   * Leave for the hub when the workspace changes underneath this page.
   *
   * Switching workspaces keeps the current path and only swaps `?ws=`, so a campaign detail URL
   * survives into a workspace where that campaign id does not exist — and the page greeted the
   * switch with "Something went wrong / Campaign not found". A campaign belongs to exactly one
   * workspace, so there is nothing here to show; the hub is the nearest thing that still means
   * something. `replace` keeps the dead URL out of history rather than leaving Back to walk into
   * the same error.
   *
   * Guarded on a previously-RESOLVED slug: the first paint has `''` while the workspace list is
   * still loading, and treating that as a change would bounce every direct link to a campaign.
   */
  const seenTenantKey = useRef(tenantKey);
  useEffect(() => {
    if (tenantKey === '') return;
    if (seenTenantKey.current === '') {
      seenTenantKey.current = tenantKey;
      return;
    }
    if (seenTenantKey.current !== tenantKey) {
      seenTenantKey.current = tenantKey;
      navigate('/campaigns', { replace: true });
    }
  }, [tenantKey, navigate]);

  // Role for the active tenant. Empty (e.g. dev) → don't hide the actions; the API
  // still enforces the real permissions server-side.
  const roles = useMemo(
    () =>
      principal?.memberships.find((m) => m.tenantId === principal.activeTenantId)?.roles ?? [],
    [principal],
  );
  const canReview = roles.length === 0 || roles.some((r) => REVIEWER_ROLES.includes(r));
  const canSubmit = roles.length === 0 || roles.some((r) => AUTHOR_ROLES.includes(r));

  const campaignQ = useQuery({
    queryKey: ['campaign', tenantKey, id],
    queryFn: ({ signal }) => api.getCampaign(id, signal),
    enabled: tenantKey !== '' && id !== '',
  });
  const transitionsQ = useQuery({
    queryKey: ['campaign-transitions', tenantKey, id],
    queryFn: ({ signal }) => api.getCampaignTransitions(id, signal),
    enabled: tenantKey !== '' && id !== '',
  });
  const campaign = campaignQ.data;

  // The EFFECTIVE approval mode: the install-wide global mode wins unless it defers to the
  // workspace (PER_WORKSPACE). This is what decides campaign-level vs per-post approval UI — so
  // setting the global mode to Per-Post moves submission onto the individual post cards.
  const effectiveType = useEffectiveApprovalType();

  const invalidate = () => {
    // Prefix keys (no id) so a post that lands in ANOTHER campaign — duplicate/move — also
    // refreshes that campaign's cached detail view, not just the one being viewed. Otherwise a
    // still-fresh cache renders the destination without the post it just received.
    invalidateContentQueries(queryClient, tenantKey);
    void queryClient.invalidateQueries({ queryKey: ['campaign-transitions', tenantKey] });
  };

  const onError = (verb: string) => (err: unknown) =>
    void alert({
      title: 'Something went wrong',
      message: err instanceof ApiRequestError ? err.message : `Could not ${verb}.`,
    });

  // Campaign-level approval mutations are gone with the campaign-level button — approval is per LANE
  // now. The endpoints remain server-side; nothing in this page calls them.

  /**
   * Which lane is mid-request, so only that lane's bar shows as busy. One shared flag would grey out
   * all three, which reads as the whole campaign being locked by an action on one of them.
   */
  const [laneBusy, setLaneBusy] = useState<ContentChannel | null>(null);
  const laneMutation = <T,>(fn: (channel: ContentChannel, arg: T) => Promise<unknown>) => ({
    mutationFn: async ({ channel, arg }: { channel: ContentChannel; arg: T }) => {
      setLaneBusy(channel);
      try {
        return await fn(channel, arg);
      } finally {
        setLaneBusy(null);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey, id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
    },
    onError: (err: unknown) =>
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : 'Please try again.',
      }),
  });

  const submitLane = useMutation(
    laneMutation<SubmitForApprovalDto>((channel, dto) =>
      api.submitCampaignLane(id, channel, dto),
    ),
  );
  const decideLane = useMutation(
    laneMutation<ApprovalDecisionDto>((channel, dto) => api.decideCampaignLane(id, channel, dto)),
  );
  const cancelLaneReview = useMutation(
    laneMutation<void>((channel) => api.cancelCampaignLaneReview(id, channel)),
  );
  const complianceLane = useMutation(
    laneMutation<void>((channel) => api.complianceApproveCampaignLane(id, channel)),
  );
  const reopenLane = useMutation(
    laneMutation<void>((channel) => api.reopenCampaignLane(id, channel)),
  );
  // ── Per-post approval mutations (POST mode) ──
  const submitPost = useMutation({
    mutationFn: (postId: string) => api.submitPostForApproval(postId, {}),
    onSuccess: invalidate,
    onError: onError('submit the post for approval'),
  });
  const decidePost = useMutation({
    mutationFn: (vars: { postId: string; dto: ApprovalDecisionDto }) =>
      api.decidePost(vars.postId, vars.dto),
    onSuccess: invalidate,
    onError: onError('record the decision'),
  });
  const compliancePost = useMutation({
    mutationFn: (postId: string) => api.compliancePostCheck(postId),
    onSuccess: invalidate,
    onError: onError('run the compliance check'),
  });
  const cancelReviewPost = useMutation({
    mutationFn: (postId: string) => api.cancelReviewPost(postId),
    onSuccess: invalidate,
    onError: onError('withdraw the post from review'),
  });

  const removeCampaign = useMutation({
    mutationFn: () => api.deleteCampaign(id),
    onSuccess: () => {
      // Deleting the campaign trashes its posts too, so this clears the Calendar and the campaign
      // detail entry as well — Back would otherwise re-render the deleted campaign from cache.
      invalidateContentQueries(queryClient, tenantKey);
      navigate('/campaigns');
    },
    onError: onError('delete the campaign'),
  });

  const watchLostPublish = useLostPublishWatch();
  const publishPost = useMutation({
    // The failure the post already shows, if any: what a lost publish is later compared against.
    onMutate: (postId: string) => ({
      errorBefore: campaign?.posts?.find((p) => p.id === postId)?.publishError ?? null,
    }),
    mutationFn: (postId: string) => api.publishPost(postId),
    onSuccess: invalidate,
    // Publish now delivers to the native platform; on failure the API records the real
    // reason on the post (publishedAt stays null). Refresh so the card shows that reason
    // and keeps the Publish action available for a retry, then surface the message.
    onError: (err, postId, sent) => {
      invalidate();
      if (isLostRequest(err)) {
        // No answer came back, so the post may still be going out (lostRequest.ts): say that, not
        // "could not publish", and read the post again until its card can show how it ended.
        void alert({ message: LOST_REQUEST_MESSAGE });
        watchLostPublish({
          read: () => api.getPost(postId),
          errorBefore: sent?.errorBefore ?? null,
          onResult: invalidate,
          onGiveUp: invalidate,
        });
        return;
      }
      onError('publish')(err);
    },
  });
  const deletePost = useMutation({
    mutationFn: (postId: string) => api.deletePost(postId),
    onSuccess: invalidate,
    onError: onError('delete the post'),
  });

  /**
   * Composer params, starting from a clean slate but keeping `from` — that's where the user came
   * in from, and closing the composer must not silently rewrite the back link.
   */
  const composeParams = () => {
    const next = new URLSearchParams();
    const from = params.get('from');
    if (from) next.set('from', from);
    return next;
  };

  const openAddPost = () => {
    const next = composeParams();
    next.set('compose', 'new');
    next.set('campaign', id);
    setParams(next, { replace: false });
  };
  const openEditPost = (postId: string) => {
    const next = composeParams();
    next.set('compose', postId);
    setParams(next, { replace: false });
  };
  /** Open the composer straight to its Schedule tab (the post card's "Schedule" action). */
  const openSchedulePost = (postId: string) => {
    const next = composeParams();
    next.set('compose', postId);
    next.set('ctab', 'schedule');
    setParams(next, { replace: false });
  };

  if (campaignQ.isLoading) return <LoadingState label="Loading campaign…" />;
  if (campaignQ.isError || !campaign) {
  
  return (
      <ErrorState
        message={
          campaignQ.error instanceof ApiRequestError
            ? campaignQ.error.message
            : 'Could not load the campaign.'
        }
        onRetry={() => void campaignQ.refetch()}
      />
    );
  }

  const state = campaign.approvalState;
  const posts = [...(campaign.posts ?? [])].sort((a, b) => a.order - b.order);

  /**
   * One post card plus its per-post approval bar. Lifted out of the list unchanged so the three
   * lanes below can each render their own posts — this is a grouping change, not a redesign of the
   * card.
   */
  const renderPost = (post: Post) => (
            <div key={post.id} className="space-y-2">
              <PostCard
                post={post}
                campaignState={state}
                approvalType={effectiveType}
                actions={{
                  onEdit: () => openEditPost(post.id),
                  onSchedule: () => openSchedulePost(post.id),
                  onPublish: async () => {
                    if (await confirm(publishConfirm(post))) publishPost.mutate(post.id);
                  },
                  onDelete: async () => {
                    if (
                      await confirm({
                        title: 'Delete post',
                        message: `Delete "${post.title}"?`,
                        confirmLabel: 'Delete',
                        danger: true,
                      })
                    )
                      deletePost.mutate(post.id);
                  },
                  onDuplicate: () => setPicker({ action: 'duplicate', post }),
                  onMoveToCampaign: () => setPicker({ action: 'move', post }),
            onGenerateDesign: () => setDesignPost(post),
                }}
              />
              {/* Per-post approval — POST mode. The bar self-hides in terminal states. */}
              {effectiveType === ApprovalType.POST && (
                <ApprovalActionBar
                  state={post.approvalState ?? ApprovalState.DRAFT}
                  busy={postBusyFor(post.id)}
                  canReview={canReview}
                  canSubmit={canSubmit}
                  context="post"
                  submitBlockedReason={
                    post.scheduledAt == null && !post.publishWhenApproved
                      ? 'Set a proposed schedule, or choose “Post as soon as approved” (in the post editor’s Schedule tab), before submitting.'
                      : null
                  }
                  pendingApproverNames={post.pendingApproverNames}
                  onSubmit={() => submitPost.mutate(post.id)}
                  onApprove={() =>
                    decidePost.mutate({ postId: post.id, dto: { decision: 'approve' } })
                  }
                  onCancelReview={() => cancelReviewPost.mutate(post.id)}
                  onRequestChanges={() =>
                    openRequestChanges((comment) =>
                      decidePost.mutate({
                        postId: post.id,
                        dto: { decision: 'request-changes', comment },
                      }),
                    )
                  }
                  onReject={() =>
                    openReject('Reject post', (comment) =>
                      decidePost.mutate({ postId: post.id, dto: { decision: 'reject', comment } }),
                    )
                  }
                  onCompliance={() => compliancePost.mutate(post.id)}
                />
              )}
            </div>
  );

  /**
   * The lanes the campaign page groups by, in the order the business works in: the blog is written
   * and published first (it dictates the social copy and provides the link the social posts share),
   * then social, then video.
   *
   * Every heading shows even when its lane is empty, so a campaign's shape is legible at a glance
   * rather than inferred from what happens to be there.
   */
  const LANE_LABEL: Partial<Record<ContentChannel, string>> = {
    [ContentChannel.BLOG]: 'Blog posts',
    [ContentChannel.SOCIAL]: 'Social posts',
    [ContentChannel.VIDEO]: 'Video posts',
    [ContentChannel.PODCAST]: 'Podcast posts',
  };
  /**
   * The three lanes always shown, plus any OTHER channel this campaign actually holds.
   *
   * The extras matter more than they look: campaign approval is per lane now, with no campaign-level
   * button behind it, so a podcast post from an older campaign would have no route to approval at all
   * if its channel were not a lane of its own.
   */
  const LANES: { channel: ContentChannel; label: string }[] = [
    ContentChannel.BLOG,
    ContentChannel.SOCIAL,
    ContentChannel.VIDEO,
    ...[...new Set(posts.map((p) => p.channel))].filter(
      (ch) =>
        ch !== ContentChannel.BLOG && ch !== ContentChannel.SOCIAL && ch !== ContentChannel.VIDEO,
    ),
  ].map((channel) => ({ channel, label: LANE_LABEL[channel] ?? `${enumLabel(channel)} posts` }));
  /**
   * Each lane's own approval state, derived server-side from that lane's posts.
   *
   * Shown per lane because in Campaign mode the lanes are approved separately — the blog is signed
   * off and published ahead of the social content that links to it — so one campaign-level badge
   * cannot describe where the work actually stands.
   */
  const laneStateFor = (channel: ContentChannel) =>
    campaign.lanes?.find((l) => l.channel === channel)?.state ?? null;

  const askReopenLane = async (channel: ContentChannel, label: string) => {
    const ok = await confirm({
      title: `Reopen ${label.toLowerCase()} for editing?`,
      message:
        'The lane goes back to draft and its posts lose their scheduled times, so nothing publishes until it is approved and scheduled again. Posts that have already gone out are left alone.',
      confirmLabel: 'Reopen',
    });
    if (ok) reopenLane.mutate({ channel, arg: undefined });
  };

  /**
   * What this lane's approval bar says, and whether it may be submitted.
   *
   * The lane's OWN readiness decides — an unscheduled blog post must not stop the social lane being
   * submitted. Mirrors the server's per-lane gate.
   *
   * Anything still waiting is listed rather than run together in a sentence: three posts joined by
   * commas is a wall of text you have to parse, and the whole reason to show names is so someone can
   * scan for the one they forgot.
   */
  const laneSubmitNote = (
    channel: ContentChannel,
    label: string,
  ): { blocked: ReactNode | null; ready: ReactNode } => {
    // "Blog posts" → "the blog posts are ready".
    const ready = `When the ${label.toLowerCase()} are ready, submit for review.`;
    const lanePosts = posts.filter((p) => p.channel === channel);
    if (lanePosts.length === 0) {
      return { blocked: 'There are no posts in this lane yet.', ready };
    }
    const notReady = lanePosts.filter(
      (p) =>
        p.scheduledAt == null &&
        !p.publishWhenApproved &&
        // An export post never goes out, so a missing date cannot hold its lane back.
        !isExportOnlyPost(p.channel, p.platformLabel),
    );
    if (notReady.length === 0) return { blocked: null, ready };
    return {
      blocked: (
        <>
          <p>{ready}</p>
          <p className="mt-1">Still to schedule:</p>
          <ul className="mt-0.5 list-disc pl-5">
            {notReady.map((p) => (
              <li key={p.id}>{p.title?.trim() || 'Untitled post'}</li>
            ))}
          </ul>
        </>
      ),
      ready,
    };
  };
  /**
   * The status a post SHOWS on this page.
   *
   * Deliberately the card's own helper, fed this campaign's state. The hub's equivalent reads
   * `post.campaignApprovalState`, which the campaign-DETAIL payload does not carry — using it here
   * made every post look like a DRAFT in Campaign mode and the sort did nothing at all.
   */
  const chipStateOf = (p: Post): PostDisplayState =>
    failureChipState(p) ?? postDisplayState(p, state, effectiveType);

  /**
   * One lane's posts, ordered by status.
   *
   * `posts` is already in manual `order`, and sort is stable, so this groups by status while
   * leaving the author's arrangement intact inside each group. Sorting the FILTERED copy matters —
   * sorting `posts` itself would reorder the shared array every lane reads.
   */
  const postsIn = (channel: ContentChannel) =>
    posts
      .filter((p) => p.channel === channel)
      .sort((a, b) => postStatusRank(chipStateOf(a)) - postStatusRank(chipStateOf(b)));
  // DISPLAY ONLY. With approvals off there is no review chain to move the campaign off DRAFT, so
  // the header read "Draft" even once every post was scheduled. `state` above is deliberately left
  // as the campaign's REAL stored state — it gates the approval actions, and those must not be
  // driven by a derived value.
  const badgeState = campaignBadgeState(
    state,
    posts.map((p) => postBadgeState(p, effectiveType)),
    effectiveType,
  );
  // NOTE: the campaign-level submit gate and busy flag are gone with the campaign-level button.
  // Readiness is per LANE now (laneSubmitBlockedReason), and only the acting lane shows as busy.
  // The campaign-level mutations remain because their endpoints do — nothing in this page calls them.
  // Per-post busy — only the acted-on post's bar shows loading (not every post's).
  const postBusyFor = (postId: string): boolean =>
    (submitPost.isPending && submitPost.variables === postId) ||
    (decidePost.isPending && decidePost.variables?.postId === postId) ||
    (compliancePost.isPending && compliancePost.variables === postId) ||
    (cancelReviewPost.isPending && cancelReviewPost.variables === postId);
  const isDraftLike = SUBMITTABLE_STATES.has(state);
  // Adding a post is gated on the MODE, not simply the aggregate. CAMPAIGN mode locks
  // the campaign as a whole once it leaves DRAFT/REVISE. POST mode lets a new post start
  // its own DRAFT lifecycle, so a sibling under review (aggregate → IN_REVIEW) must not
  // block adds — but the server (assertCampaignAcceptsNewPost) still refuses once the
  // campaign aggregate is terminal (PUBLISHED/REJECTED). NONE is never gated server-side.
  const canAddPost =
    effectiveType === ApprovalType.CAMPAIGN
      ? isDraftLike
      : effectiveType === ApprovalType.POST
        ? state !== ApprovalState.PUBLISHED && state !== ApprovalState.REJECTED
        : true;

  /** Open the comment modal for a request-changes / reject decision (campaign or a post). */
  const openRequestChanges = (run: (comment: string) => void) =>
    setPendingDecision({
      title: 'Request changes',
      confirmLabel: 'Request changes',
      onConfirm: (comment) => {
        run(comment);
        setPendingDecision(null);
      },
    });
  const openReject = (label: string, run: (comment: string) => void) =>
    setPendingDecision({
      title: label,
      confirmLabel: 'Reject',
      danger: true,
      onConfirm: (comment) => {
        run(comment);
        setPendingDecision(null);
      },
    });

  // Send the user back to the list they actually arrived from. `?from=posts` is set by the
  // campaign link in the All-Posts table; anything else (a campaign card, a pasted URL) falls back
  // to the campaign list, which is where those journeys started.
  const cameFromPosts = params.get('from') === 'posts';

  return (
    <>
      <Link
        to={cameFromPosts ? '/campaigns?view=posts' : '/campaigns'}
        className="focus-ring mb-3 inline-flex items-center gap-1 rounded text-sm font-medium text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {cameFromPosts ? 'All posts' : 'All campaigns'}
      </Link>

      <PageHeader
        title={campaign.name}
        description={campaign.description || undefined}
        actions={
          <div className="flex items-center gap-3">
            <Badge tone={approvalStateTone[badgeState]} size="xl">
              {approvalStateLabel[badgeState]}
            </Badge>
            {isDraftLike && (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                Edit details
              </Button>
            )}
            {state === ApprovalState.DRAFT && (
              <Button
                variant="ghost"
                size="sm"
                loading={removeCampaign.isPending}
                onClick={async () => {
                  if (
                    await confirm({
                      title: 'Delete campaign',
                      message: `Delete "${campaign.name}" and all its posts?`,
                      confirmLabel: 'Delete',
                      danger: true,
                    })
                  )
                    removeCampaign.mutate();
                }}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </Button>
            )}
          </div>
        }
      />

      <TestModeBanner />

      {/*
        Said once for the campaign, rather than leaving it to be inferred from chips on individual
        posts. Nothing will publish these now — their time went by while they waited — and the only
        fix is a new one.
      */}
      {posts.some((p) => isPostOverdue(p)) && (
        <div className="mb-5">
          <AlertBanner tone="warning">
            One or more posts are scheduled for a date and time that has already passed. Please
            reschedule them for a future date and time.
          </AlertBanner>
        </div>
      )}

      {campaign.messaging && (
        <Card className="mb-5">
          <CardBody>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
              Messaging
            </p>
            <p className="whitespace-pre-wrap text-sm text-ink-strong">{campaign.messaging}</p>
          </CardBody>
        </Card>
      )}

      {/*
        No campaign-level approval control. Campaign approval IS per lane now — Blog, Social and Video
        are signed off separately, so a single button here would either approve work nobody looked at
        as a group, or sit disabled describing a gate that no longer exists. The campaign's badge
        remains what it always was: a rollup of its posts.

        In POST mode the campaign status is likewise the aggregate of its posts (submit/approve on
        each post card below); NONE has no gate.
      */}
      {effectiveType === ApprovalType.CAMPAIGN && (
        <ActionRow note="Approved by group — submit and approve each group of posts below. This campaign's status is the aggregate of its posts.">
          <Badge tone={approvalStateTone[badgeState]}>{approvalStateLabel[badgeState]}</Badge>
        </ActionRow>
      )}
      {effectiveType === ApprovalType.POST && (
        <ActionRow note="Approved per-post — this campaign's status is the aggregate of its posts. Submit and approve each post below.">
          <Badge tone={approvalStateTone[badgeState]}>{approvalStateLabel[badgeState]}</Badge>
        </ActionRow>
      )}

      {/* Posts */}
      <div className="mb-3 mt-6 flex items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Posts ({posts.length})
        </h2>
        {canAddPost && (
          <Button size="sm" onClick={openAddPost}>
            <Plus className="h-4 w-4" aria-hidden />
            Add Post
          </Button>
        )}
      </div>

      {posts.length === 0 ? (
        <EmptyState
          title="No posts yet"
          description={
            canAddPost
              ? 'Add a post to this campaign to start creating content.'
              : 'This campaign has no posts.'
          }
          action={
            canAddPost ? (
              <Button onClick={openAddPost}>
                <Plus className="h-4 w-4" aria-hidden />
                Add Post
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-8">
          {LANES.map((lane) => {
            const lanePosts = postsIn(lane.channel);
            return (
              <section key={lane.channel}>
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {lane.label} ({lanePosts.length})
                  </h3>
                  {/* Only when the lane HAS posts — an empty lane has no state, and a badge on one
                      would invent a status for work that does not exist. */}
                  {effectiveType === ApprovalType.CAMPAIGN && laneStateFor(lane.channel) && (
                    <Badge tone={approvalStateTone[laneStateFor(lane.channel)!]} size="sm">
                      {approvalStateLabel[laneStateFor(lane.channel)!]}
                    </Badge>
                  )}
                </div>
                {/* One lane, one approval. Blog is signed off and published ahead of the social
                    content that links to it, so the lanes cannot share a single control. */}
                {effectiveType === ApprovalType.CAMPAIGN && lanePosts.length > 0 && (
                  <div className="mb-3">
                    <ApprovalActionBar
                      state={laneStateFor(lane.channel) ?? ApprovalState.DRAFT}
                      busy={laneBusy === lane.channel}
                      canReview={canReview}
                      canSubmit={canSubmit}
                      context="campaign"
                      submitBlockedReason={laneSubmitNote(lane.channel, lane.label).blocked}
                      readySubmitNote={laneSubmitNote(lane.channel, lane.label).ready}
                      onSubmit={() =>
                        submitLane.mutate({ channel: lane.channel, arg: {} })
                      }
                      onApprove={() =>
                        decideLane.mutate({ channel: lane.channel, arg: { decision: 'approve' } })
                      }
                      onCancelReview={() =>
                        cancelLaneReview.mutate({ channel: lane.channel, arg: undefined })
                      }
                      onRequestChanges={() =>
                        openRequestChanges((comment) =>
                          decideLane.mutate({
                            channel: lane.channel,
                            arg: { decision: 'request-changes', comment },
                          }),
                        )
                      }
                      onReject={() =>
                        openReject(`Reject ${lane.label.toLowerCase()}`, (comment) =>
                          decideLane.mutate({
                            channel: lane.channel,
                            arg: { decision: 'reject', comment },
                          }),
                        )
                      }
                      onCompliance={() =>
                        complianceLane.mutate({ channel: lane.channel, arg: undefined })
                      }
                    />
                    {/*
                      The way back. An approved or scheduled lane refuses content edits, and
                      cancel-review only covers a lane still UNDER review — so without this the only
                      route back was a reviewer requesting changes. Plans move at an hour's notice,
                      which is the whole reason the blog lane goes first.
                    */}
                    {(laneStateFor(lane.channel) === ApprovalState.APPROVED ||
                      laneStateFor(lane.channel) === ApprovalState.SCHEDULED) && (
                      <div className="mt-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={laneBusy === lane.channel}
                          onClick={() => void askReopenLane(lane.channel, lane.label)}
                        >
                          Reopen {lane.label.toLowerCase()} for editing
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {lanePosts.length === 0 ? (
                  // Stated rather than left blank: an empty heading reads as something failing to
                  // load, and the lane's emptiness is itself information about the campaign.
                  <p className="text-sm text-ink-faint">
                    No {lane.label.toLowerCase().replace(' posts', '')} posts in this campaign yet.
                  </p>
                ) : (
                  <div className="space-y-3">{lanePosts.map((post) => renderPost(post))}</div>
                )}
              </section>
            );
          })}

        </div>
      )}

      {/* Approval history — collapsible (expanded by default). */}
      {(transitionsQ.data?.length ?? 0) > 0 && (
        <Card className="mt-6">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-expanded={historyOpen}
            className="focus-ring flex w-full items-center justify-between gap-4 rounded-xl px-5 py-4 text-left"
          >
            <span className="text-base font-semibold text-ink">
              Approval history{' '}
              <span className="font-normal text-ink-faint">({transitionsQ.data!.length})</span>
            </span>
            <ChevronDown
              className={`h-5 w-5 shrink-0 text-ink-faint transition ${historyOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
          {historyOpen && (
            <CardBody className="space-y-2 border-t border-line-soft">
              {transitionsQ.data!.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="text-ink-faint">{formatDateTime(t.createdAt)}</span>
                  {t.postTitle && (
                    <span className="rounded bg-chip px-1.5 py-0.5 text-xs font-medium text-ink-label">
                      {t.postTitle}
                    </span>
                  )}
                  <span className="font-medium text-ink-strong">
                    {t.fromState ? `${enumLabel(t.fromState)} → ` : ''}
                    {enumLabel(t.toState)}
                  </span>
                  {t.actorName && (
                    <span className="text-ink-muted">
                      · by <span className="font-medium text-ink-label">{t.actorName}</span>
                    </span>
                  )}
                  {t.comment && <span className="text-ink-muted">· “{t.comment}”</span>}
                </div>
              ))}
            </CardBody>
          )}
        </Card>
      )}

      <PostGenerateDesignModal post={designPost} onClose={() => setDesignPost(null)} />
      <CampaignFormModal open={editing} onClose={() => setEditing(false)} campaign={campaign} />
      <DecisionCommentModal target={pendingDecision} onClose={() => setPendingDecision(null)} />
      {picker && (
        <CampaignPickerModal
          open
          action={picker.action}
          post={picker.post}
          onClose={() => setPicker(null)}
          onDone={(result) => {
            invalidate();
            // The copy is a fresh DRAFT that may live in ANOTHER campaign (so it wouldn't show up
            // on this page at all) — open it in the composer as visible confirmation.
            if (picker.action === 'duplicate') openEditPost(result.id);
          }}
        />
      )}
    </>
  );
}
